import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser } from '../utils/auth';
import {
  PageHeader,
  ErrorState,
  type SortDirection,
} from '../components/common';

interface CustomerUsageRow {
  id: string;
  customer_tag: string;
  name: string;
  active: boolean;
  user_type: string;
  created_at: string;
  subscription_tier?: string;
  subscription_status?: string;
  email?: string;
  total_tokens: number;
  function_tokens: number;
  chain_tokens: number;
  total_runs: number;
  function_runs: number;
  chain_runs: number;
  functions_created: number;
  chains_created: number;
  last_activity?: string;
  avg_monthly_tokens: number;
}

interface FunctionDetail {
  id: string;
  name: string;
  created_at?: string;
  total_runs: number;
  total_tokens: number;
  avg_tokens_per_run: number;
  avg_execution_time_ms: number;
  success_rate: number;
  last_run?: string;
  most_used_model?: string;
}

interface ChainDetail {
  id: string;
  name: string;
  created_at?: string;
  total_executions: number;
  total_tokens: number;
  avg_tokens_per_execution: number;
  avg_execution_time_ms: number;
  last_execution?: string;
}

interface UsageDetail {
  customer: {
    id: string;
    customer_tag: string;
    name: string;
    subscription_tier?: string;
    subscription_status?: string;
    subscription_total_credits?: number;
  };
  summary: {
    total_tokens: number;
    function_tokens: number;
    chain_tokens: number;
    total_runs: number;
    function_runs: number;
    chain_runs: number;
    functions_created: number;
    chains_created: number;
  };
  functions: FunctionDetail[];
  chains: ChainDetail[];
}

function formatNumber(n?: number): string {
  if (n === undefined || n === null) return '0';
  return n.toLocaleString();
}

function formatDate(dateString?: string): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDuration(ms?: number): string {
  if (!ms) return '-';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

interface ColumnDef {
  id: string;
  label: string;
  sortable?: boolean;
  numeric?: boolean;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
}

const COLUMNS: ColumnDef[] = [
  { id: 'expand', label: '', sortable: false, width: 48 },
  { id: 'customer_tag', label: 'Customer', sortable: true },
  { id: 'email', label: 'Email', sortable: false },
  { id: 'total_tokens', label: 'Total Tokens', sortable: true, numeric: true },
  { id: 'function_tokens', label: 'Fn Tokens', sortable: true, numeric: true },
  { id: 'chain_tokens', label: 'Chain Tokens', sortable: true, numeric: true },
  { id: 'total_runs', label: 'Total Runs', sortable: true, numeric: true },
  { id: 'functions_created', label: 'Functions', sortable: true, numeric: true },
  { id: 'chains_created', label: 'Chains', sortable: true, numeric: true },
  { id: 'avg_monthly_tokens', label: 'Avg/Month', sortable: false, numeric: true },
  { id: 'last_activity', label: 'Last Activity', sortable: true },
  { id: 'subscription_tier', label: 'Tier', sortable: false },
  { id: 'actions', label: '', sortable: false, width: 90, align: 'center' },
];

const TOTAL_COL_COUNT = COLUMNS.length;

export const AdminUsageDashboard: React.FC = () => {
  const { user, permissions } = useAuth();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState<CustomerUsageRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [sortBy, setSortBy] = useState<string>('total_tokens');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailData, setDetailData] = useState<UsageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!isAdminUser(permissions?.userType)) {
      navigate('/dashboard');
    }
  }, [permissions, navigate]);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const idToken = await user?.getIdToken();
      const params = new URLSearchParams({
        page: String(page + 1),
        page_size: String(rowsPerPage),
        search,
        sort_by: sortBy,
        sort_dir: sortDir,
        active_only: String(activeOnly),
      });

      const res = await fetch(`/api/v2/admin/usage-dashboard?${params}`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      setCustomers(data.data || []);
      setTotalCount(data.total || 0);
    } catch (e: any) {
      setError(e?.message || 'Failed to load usage dashboard');
    } finally {
      setLoading(false);
    }
  }, [user, page, rowsPerPage, search, sortBy, sortDir, activeOnly]);

  useEffect(() => {
    if (user && isAdminUser(permissions?.userType)) {
      loadCustomers();
    }
  }, [user, permissions, page, rowsPerPage, sortBy, sortDir, activeOnly, loadCustomers]);

  const handleSearch = () => {
    setPage(0);
    loadCustomers();
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
    setPage(0);
  };

  const loadDetail = async (customerId: string) => {
    setDetailLoading(true);
    setDetailData(null);
    try {
      const idToken = await user?.getIdToken();
      const res = await fetch(`/api/v2/admin/customers/${customerId}/usage-detail`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setDetailData(json.data);
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Failed to load detail', severity: 'error' });
      setExpandedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleToggleExpand = (customerId: string) => {
    if (expandedId === customerId) {
      setExpandedId(null);
      setDetailData(null);
    } else {
      setExpandedId(customerId);
      loadDetail(customerId);
    }
  };

  const renderDetailContent = () => {
    if (detailLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!detailData) return null;

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {detailData.customer.name} — Usage Breakdown
        </Typography>

        {/* Summary cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Tokens', value: formatNumber(detailData.summary.total_tokens) },
            { label: 'Function Tokens', value: formatNumber(detailData.summary.function_tokens) },
            { label: 'Chain Tokens', value: formatNumber(detailData.summary.chain_tokens) },
            { label: 'Total Runs', value: formatNumber(detailData.summary.total_runs) },
            { label: 'Functions', value: formatNumber(detailData.summary.functions_created) },
            { label: 'Chains', value: formatNumber(detailData.summary.chains_created) },
          ].map((stat) => (
            <Grid item xs={6} sm={4} md={2} key={stat.label}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                  <Typography variant="h6">{stat.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Functions table */}
        {detailData.functions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              AI Functions ({detailData.functions.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Runs</TableCell>
                    <TableCell align="right">Total Tokens</TableCell>
                    <TableCell align="right">Avg Tokens/Run</TableCell>
                    <TableCell align="right">Avg Duration</TableCell>
                    <TableCell align="right">Success Rate</TableCell>
                    <TableCell>Last Run</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell align="center" width={40}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailData.functions.map((fn) => (
                    <TableRow key={fn.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{fn.name}</Typography>
                      </TableCell>
                      <TableCell align="right">{formatNumber(fn.total_runs)}</TableCell>
                      <TableCell align="right">{formatNumber(fn.total_tokens)}</TableCell>
                      <TableCell align="right">{formatNumber(Math.round(fn.avg_tokens_per_run))}</TableCell>
                      <TableCell align="right">{formatDuration(fn.avg_execution_time_ms)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${fn.success_rate}%`}
                          size="small"
                          color={fn.success_rate >= 90 ? 'success' : fn.success_rate >= 70 ? 'warning' : 'error'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{formatDate(fn.last_run)}</TableCell>
                      <TableCell>
                        <Typography variant="caption">{fn.most_used_model || '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Run History">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/dashboard/function-runs/${fn.id}?customer_id=${expandedId}&customer_tag=${detailData.customer.customer_tag}`
                              )
                            }
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Chains table */}
        {detailData.chains.length > 0 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Function Chains ({detailData.chains.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Executions</TableCell>
                    <TableCell align="right">Total Tokens</TableCell>
                    <TableCell align="right">Avg Tokens/Exec</TableCell>
                    <TableCell align="right">Avg Duration</TableCell>
                    <TableCell>Last Execution</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailData.chains.map((chain) => (
                    <TableRow key={chain.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{chain.name}</Typography>
                      </TableCell>
                      <TableCell align="right">{formatNumber(chain.total_executions)}</TableCell>
                      <TableCell align="right">{formatNumber(chain.total_tokens)}</TableCell>
                      <TableCell align="right">{formatNumber(Math.round(chain.avg_tokens_per_execution))}</TableCell>
                      <TableCell align="right">{formatDuration(chain.avg_execution_time_ms)}</TableCell>
                      <TableCell>{formatDate(chain.last_execution)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {detailData.functions.length === 0 && detailData.chains.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No functions or chains created yet.
          </Typography>
        )}
      </Box>
    );
  };

  if (error && !loading) {
    return (
      <Box>
        <PageHeader title="Usage Dashboard" subtitle="Platform usage overview by customer" />
        <ErrorState error={error} onRetry={loadCustomers} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Usage Dashboard"
        subtitle="Platform usage overview — customers ranked by token consumption"
        actions={[
          { label: 'Refresh', onClick: () => { setExpandedId(null); loadCustomers(); }, icon: <RefreshIcon />, variant: 'outlined', disabled: loading },
        ]}
        searchValue={search}
        onSearchChange={setSearch}
        onSearch={handleSearch}
        searchPlaceholder="Search by name, tag, email, or ID"
        showSearch
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <FormControlLabel
          control={<Switch checked={activeOnly} onChange={(e) => { setActiveOnly(e.target.checked); setPage(0); }} size="small" />}
          label="Active customers only"
        />
      </Stack>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align || (col.numeric ? 'right' : 'left')}
                    style={{ width: col.width }}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={sortBy === col.id}
                        direction={sortBy === col.id ? sortDir : 'asc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={TOTAL_COL_COUNT} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TOTAL_COL_COUNT} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">No customers found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((row) => {
                  const isExpanded = expandedId === row.id;
                  return (
                    <React.Fragment key={row.id}>
                      {/* Main data row */}
                      <TableRow hover sx={{ '& > td': { borderBottom: isExpanded ? 'none' : undefined } }}>
                        {/* Expand toggle */}
                        <TableCell sx={{ width: 48 }}>
                          <IconButton size="small" onClick={() => handleToggleExpand(row.id)}>
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>

                        {/* Customer */}
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{row.customer_tag}</Typography>
                          {row.name !== row.customer_tag && (
                            <Typography variant="caption" color="text.secondary">{row.name}</Typography>
                          )}
                        </TableCell>

                        {/* Email */}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{row.email || '-'}</Typography>
                        </TableCell>

                        {/* Total Tokens */}
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>{formatNumber(row.total_tokens)}</Typography>
                        </TableCell>

                        {/* Fn Tokens */}
                        <TableCell align="right">{formatNumber(row.function_tokens)}</TableCell>

                        {/* Chain Tokens */}
                        <TableCell align="right">{formatNumber(row.chain_tokens)}</TableCell>

                        {/* Total Runs */}
                        <TableCell align="right">{formatNumber(row.total_runs)}</TableCell>

                        {/* Functions */}
                        <TableCell align="right">{formatNumber(row.functions_created)}</TableCell>

                        {/* Chains */}
                        <TableCell align="right">{formatNumber(row.chains_created)}</TableCell>

                        {/* Avg/Month */}
                        <TableCell align="right">{formatNumber(row.avg_monthly_tokens)}</TableCell>

                        {/* Last Activity */}
                        <TableCell>{formatDate(row.last_activity)}</TableCell>

                        {/* Tier */}
                        <TableCell>
                          {row.subscription_tier ? (
                            <Chip
                              label={row.subscription_tier}
                              size="small"
                              color={row.subscription_status === 'active' ? 'success' : 'default'}
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="View Run History">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/dashboard/function-runs?customer_id=${row.id}&customer_tag=${row.customer_tag}`)}
                              >
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Customer">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/dashboard/admin/customers`)}
                              >
                                <PersonIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {/* Expandable detail row */}
                      <TableRow>
                        <TableCell
                          colSpan={TOTAL_COL_COUNT}
                          sx={{ py: 0, px: 0, borderBottom: isExpanded ? undefined : 'none' }}
                        >
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 3, bgcolor: 'action.hover' }}>
                              {renderDetailContent()}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && customers.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsageDashboard;
